/**
 * SuperAdminDashboard Page
 *
 * Main dashboard for super administrators with platform overview,
 * quick actions, recent activity, usage charts, system health,
 * platform data statistics, and admin settings.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Building2,
  TrendingUp,
  Activity,
  UserPlus,
  Plus,
  Eye,
  RefreshCw,
  DollarSign,
  BarChart3,
  PieChart,
  Clock,
  AlertCircle,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  Server,
  Database,
  Zap,
  Mail,
  Truck,
  FileText,
  Hash,
  CheckCircle,
  Shield,
  Link2,
  Globe,
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { LoadingState } from '../components/shared/LoadingState';
import { Card, MetricCard, CardGrid } from '../components/shared/Card';
import { SubscriptionBadge } from '../components/admin/SubscriptionBadge';
import { superAdminApi } from '../services/adminApi';
import { dashboardApi } from '../services/api';
import type { DashboardStats, PlatformUsageStats, AuditLogEntry } from '../services/adminApi';

interface AdminStatsData {
  totalCounts: {
    total_carriers: number;
    carriers_with_inspections: number;
    total_inspections: number;
    total_violations: number;
    total_crashes: number;
    total_unique_vins: number;
    carriers_with_violations: number;
    carriers_with_crashes: number;
  };
  dataFreshness: {
    latest_inspection_date: string;
    latest_crash_date: string;
    last_inspection_import: string;
    last_crash_import: string;
    inspections_imported_24h: number;
    violations_imported_24h: number;
    crashes_imported_24h: number;
  };
  recentActivity: {
    new_carriers_last_week: number;
    recent_inspections_30_days: number;
    recent_crashes_30_days: number;
    recent_violations_30_days: number;
  };
}

interface SystemHealthData {
  apiStatus: string;
  databaseStatus: string;
  cacheHitRate: number;
  errorRate: number;
}

export const SuperAdminDashboard: React.FC = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [platformUsage, setPlatformUsage] = useState<PlatformUsageStats | null>(null);
  const [recentActivity, setRecentActivity] = useState<AuditLogEntry[]>([]);
  const [usagePeriod, setUsagePeriod] = useState<'7d' | '30d' | '90d'>('30d');

  // IP match and entity view state
  const [ipMatches, setIpMatches] = useState<any[]>([]);
  const [entityViews, setEntityViews] = useState<any[]>([]);

  // New state for merged admin data
  const [adminStats, setAdminStats] = useState<AdminStatsData | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [systemHealth, setSystemHealth] = useState<SystemHealthData | null>(null);

  useEffect(() => {
    fetchDashboardData();
  }, [usagePeriod]);

  useEffect(() => {
    fetchAdminStats();
    fetchSystemHealth();
    fetchIpMatches();
    fetchEntityViews();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [stats, usage, activity] = await Promise.all([
        superAdminApi.getDashboardStats(),
        superAdminApi.getPlatformUsage(usagePeriod),
        superAdminApi.getRecentAdminActions(),
      ]);
      setDashboardStats(stats);
      setPlatformUsage(usage);
      setRecentActivity(activity);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminStats = async () => {
    try {
      const response = await fetch('/api/admin/stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setAdminStats(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching admin stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchSystemHealth = async () => {
    try {
      const data = await dashboardApi.getAdminDashboard();
      if (data?.systemHealth) {
        setSystemHealth(data.systemHealth);
      }
    } catch (error) {
      console.error('Error fetching system health:', error);
    }
  };

  const fetchIpMatches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/ip-matches', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) setIpMatches(result.data.matches || []);
      }
    } catch (error) {
      console.error('Error fetching IP matches:', error);
    }
  };

  const fetchEntityViews = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/entity-views?limit=20', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) setEntityViews(result.data.views || []);
      }
    } catch (error) {
      console.error('Error fetching entity views:', error);
    }
  };

  const COLORS = ['#6d685e', '#23211d', '#5d7148', '#b07d2b']; // Washi categorical ramp

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      'user.created': 'Created user',
      'user.updated': 'Updated user',
      'user.deleted': 'Deleted user',
      'USER_DELETED': 'Deleted user',
      'USER_CREATED': 'Created user',
      'USER_UPDATED': 'Updated user',
      'company.created': 'Created company',
      'company.updated': 'Updated company',
      'company.deleted': 'Deleted company',
      'COMPANY_CREATED': 'Created company',
      'COMPANY_UPDATED': 'Updated company',
      'subscription.changed': 'Changed subscription',
      'SUBSCRIPTION_CHANGED': 'Subscription changed',
      'UNAUTHORIZED_ACCESS_ATTEMPT': 'Unauthorized access attempt',
      'LOGIN_SUCCESS': 'User logged in',
      'LOGIN_FAILED': 'Failed login attempt',
      'PASSWORD_RESET': 'Password reset',
      'ROLE_CHANGED': 'Role changed',
      'PERMISSION_CHANGED': 'Permission changed',
    };
    return labels[action] || action;
  };


  const formatTierName = (tier: string) => {
    if (!tier || tier === 'null' || tier === 'free') return 'Free';
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  };

  const formatFeatureName = (type: string) => {
    const labels: Record<string, string> = {
      'carrier_search': 'Carrier Search',
      'carrier_profile_view': 'Carrier Profile',
      'inspection_lookup': 'Inspection Lookup',
      'violation_lookup': 'Violation Lookup',
      'crash_lookup': 'Crash Lookup',
      'risk_score_check': 'Risk Score',
      'fraud_check': 'Fraud Check',
      'fair_score_check': 'FAIR Score',
      'basics_lookup': 'BASICs Lookup',
      'contact_match': 'Contact Match',
      'driver_lookup': 'Driver Lookup',
      'vehicle_lookup': 'Vehicle Lookup',
      'census_lookup': 'Census Lookup',
      'carrier_monitor': 'Carrier Monitor',
      'change_history': 'Change History',
      'historic_stats': 'Historic Stats',
      'shipper_search': 'Shipper Search',
      'shipper_intelligence': 'Shipper Intel',
      'broker_load_management': 'Broker Loads',
      'broker_risk_check': 'Broker Risk Check',
      'insurance_claims': 'Insurance Claims',
      'insurance_intelligence': 'Insurance Intel',
      'data_export': 'Data Export',
      'unified_search': 'Search',
      'analytics_view': 'Analytics',
      'dashboard_view': 'Dashboard',
      'projections_view': 'Projections',
      'scoring_view': 'Scoring',
      'predictions_view': 'Predictions',
      'network_analysis': 'Network Analysis',
      'dnu_check': 'DNU Check',
      'bulk_operation': 'Bulk Operations',
      'alert_management': 'Alerts',
      'other_api_call': 'Other',
    };
    return labels[type] || type.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
  };

  const systemHealthScore = systemHealth
    ? (systemHealth.apiStatus === 'healthy' ? 25 : 0) +
      (systemHealth.databaseStatus === 'connected' ? 25 : 0) +
      (systemHealth.cacheHitRate > 0.8 ? 25 : systemHealth.cacheHitRate > 0.5 ? 15 : 5) +
      (systemHealth.errorRate < 0.01 ? 25 : systemHealth.errorRate < 0.05 ? 15 : 5)
    : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <LoadingState message="Loading dashboard..." size="lg" fullScreen />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Super Admin Dashboard
            </h1>
            <p className="text-gray-600 mt-1">
              Platform overview and management
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => { fetchDashboardData(); fetchAdminStats(); fetchSystemHealth(); }}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh data"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
            <select
              value={usagePeriod}
              onChange={(e) => setUsagePeriod(e.target.value as '7d' | '30d' | '90d')}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-yellow-800">{error}</p>
            </div>
          </div>
        )}

        {/* System Health */}
        {systemHealth && (
          <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Server className="w-5 h-5 text-blue-600" />
                System Health
              </h2>
              {systemHealthScore !== null && (
                <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                  {systemHealthScore}% Healthy
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Server className="w-4 h-4 text-gray-500" />
                  <span className="text-xs font-medium text-gray-500 uppercase">API Status</span>
                </div>
                <p className="text-lg font-bold text-gray-900">
                  {systemHealth.apiStatus === 'healthy' ? 'Operational' : 'Issues'}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="w-4 h-4 text-gray-500" />
                  <span className="text-xs font-medium text-gray-500 uppercase">Database</span>
                </div>
                <p className="text-lg font-bold text-gray-900">
                  {systemHealth.databaseStatus === 'connected' ? 'Connected' : 'Disconnected'}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4 text-gray-500" />
                  <span className="text-xs font-medium text-gray-500 uppercase">Cache Hit Rate</span>
                </div>
                <p className="text-lg font-bold text-gray-900">
                  {(systemHealth.cacheHitRate * 100).toFixed(1)}%
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {systemHealth.cacheHitRate > 0.8 ? 'Good' : systemHealth.cacheHitRate > 0.5 ? 'Fair' : 'Low'}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-gray-500" />
                  <span className="text-xs font-medium text-gray-500 uppercase">Error Rate</span>
                </div>
                <p className="text-lg font-bold text-gray-900">
                  {(systemHealth.errorRate * 100).toFixed(2)}%
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {systemHealth.errorRate < 0.01 ? 'Excellent' : systemHealth.errorRate < 0.05 ? 'Acceptable' : 'High'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Dashboard Overview Stats */}
        {dashboardStats && (
          <CardGrid columns={4} className="mb-8">
            <MetricCard
              title="Total Users"
              value={formatNumber(dashboardStats.totalUsers)}
              subtitle={`${dashboardStats.activeUsers} active`}
              icon={<Users className="w-6 h-6" />}
              color="blue"
              trend={dashboardStats.newUsers7d > 0 ? 'up' : undefined}
              trendValue={dashboardStats.newUsers7d > 0 ? `+${dashboardStats.newUsers7d} this week` : undefined}
            />
            <MetricCard
              title="Companies"
              value={formatNumber(dashboardStats.totalCompanies)}
              subtitle={`${dashboardStats.activeCompanies} active`}
              icon={<Building2 className="w-6 h-6" />}
              color="green"
            />
            <MetricCard
              title="API Usage (24h)"
              value={formatNumber(dashboardStats.apiUsage24h)}
              subtitle={`${(dashboardStats.errorRate24h * 100).toFixed(2)}% error rate`}
              icon={<Activity className="w-6 h-6" />}
              color="gray"
            />
          </CardGrid>
        )}

        {/* Quick Actions */}
        <Card className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            Quick Actions
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <button
              onClick={() => navigate('/super-admin/users', { state: { openCreateModal: true } })}
              className="flex items-center gap-3 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors group"
            >
              <div className="p-2 bg-blue-100 group-hover:bg-blue-200 rounded-lg transition-colors">
                <UserPlus className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-blue-900">Create User</span>
            </button>
            <button
              onClick={() => navigate('/super-admin/companies', { state: { openCreateModal: true } })}
              className="flex items-center gap-3 p-4 bg-purple-50 hover:bg-purple-100 rounded-xl transition-colors group"
            >
              <div className="p-2 bg-purple-100 group-hover:bg-purple-200 rounded-lg transition-colors">
                <Plus className="w-5 h-5 text-purple-600" />
              </div>
              <span className="text-sm font-medium text-purple-900">Create Company</span>
            </button>
            <button
              onClick={() => navigate('/super-admin/users')}
              className="flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors group"
            >
              <div className="p-2 bg-gray-100 group-hover:bg-gray-200 rounded-lg transition-colors">
                <Eye className="w-5 h-5 text-gray-600" />
              </div>
              <span className="text-sm font-medium text-gray-900">View All Users</span>
            </button>
            <button
              onClick={() => navigate('/super-admin/companies')}
              className="flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 rounded-xl transition-colors group"
            >
              <div className="p-2 bg-gray-100 group-hover:bg-gray-200 rounded-lg transition-colors">
                <Building2 className="w-5 h-5 text-gray-600" />
              </div>
              <span className="text-sm font-medium text-gray-900">View All Companies</span>
            </button>
          </div>
        </Card>

        {/* Platform Data Statistics (from /api/admin/stats - loads separately) */}
        <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Database className="w-5 h-5 text-blue-600" />
            Platform Data Statistics
          </h2>

          {statsLoading && (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 mx-auto mb-3 text-blue-500 animate-spin" />
              <p className="text-gray-600">Loading platform statistics...</p>
              <p className="text-sm text-gray-500 mt-1">(This may take up to 45 seconds on first load)</p>
            </div>
          )}

          {!statsLoading && !adminStats && (
            <div className="text-center py-8">
              <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-yellow-500" />
              <p className="text-gray-600">Failed to load platform statistics</p>
            </div>
          )}

          {adminStats && !statsLoading && (
            <div>
              {/* Total Records Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div className="p-4 bg-blue-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Truck className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-gray-700">Total Carriers</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">
                    {adminStats.totalCounts.total_carriers.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {adminStats.totalCounts.carriers_with_inspections.toLocaleString()} with inspections
                  </p>
                </div>

                <div className="p-4 bg-yellow-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-yellow-600" />
                    <span className="text-sm font-medium text-gray-700">Inspections</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">
                    {adminStats.totalCounts.total_inspections.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {adminStats.totalCounts.total_violations.toLocaleString()} violations
                  </p>
                </div>

                <div className="p-4 bg-red-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    <span className="text-sm font-medium text-gray-700">Crashes</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">
                    {adminStats.totalCounts.total_crashes.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {adminStats.totalCounts.carriers_with_crashes.toLocaleString()} carriers
                  </p>
                </div>

                <div className="p-4 bg-purple-50 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <Hash className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-gray-700">Unique VINs</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">
                    {adminStats.totalCounts.total_unique_vins.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {adminStats.totalCounts.carriers_with_violations.toLocaleString()} carriers w/ violations
                  </p>
                </div>
              </div>

              {/* Data Freshness */}
              <div className="border-t border-gray-200 pt-6 mb-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-green-600" />
                  Data Freshness (Last 24h)
                </h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-xl font-bold text-green-900">
                      +{adminStats.dataFreshness.inspections_imported_24h.toLocaleString()}
                    </p>
                    <p className="text-xs text-green-700 mt-1">Inspections</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <p className="text-xl font-bold text-orange-900">
                      +{adminStats.dataFreshness.violations_imported_24h.toLocaleString()}
                    </p>
                    <p className="text-xs text-orange-700 mt-1">Violations</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <p className="text-xl font-bold text-red-900">
                      +{adminStats.dataFreshness.crashes_imported_24h.toLocaleString()}
                    </p>
                    <p className="text-xs text-red-700 mt-1">Crashes</p>
                  </div>
                </div>
              </div>

              {/* 30-Day Activity */}
              <div className="border-t border-gray-200 pt-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-600" />
                  30-Day Activity
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-xl font-bold text-blue-900">
                      {adminStats.recentActivity.recent_inspections_30_days.toLocaleString()}
                    </p>
                    <p className="text-xs text-blue-700 mt-1">Inspections</p>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <p className="text-xl font-bold text-orange-900">
                      {adminStats.recentActivity.recent_violations_30_days.toLocaleString()}
                    </p>
                    <p className="text-xs text-orange-700 mt-1">Violations</p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-lg">
                    <p className="text-xl font-bold text-red-900">
                      {adminStats.recentActivity.recent_crashes_30_days.toLocaleString()}
                    </p>
                    <p className="text-xs text-red-700 mt-1">Crashes</p>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <p className="text-xl font-bold text-purple-900">
                      {adminStats.recentActivity.new_carriers_last_week.toLocaleString()}
                    </p>
                    <p className="text-xs text-purple-700 mt-1">New Carriers (7d)</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Usage Chart - Daily Trend */}
          <Card className="lg:col-span-2">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-600" />
              Daily Usage Trend
            </h2>
            <div className="h-80">
              {platformUsage?.dailyUsage && platformUsage.dailyUsage.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={platformUsage.dailyUsage}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2ddd1" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getMonth() + 1}/${date.getDate()}`;
                      }}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fbf9f4',
                        border: '1px solid #e2ddd1',
                        borderRadius: '8px',
                      }}
                      labelFormatter={(value) => new Date(value).toLocaleDateString()}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="apiCalls"
                      stroke="#5d7148"
                      strokeWidth={2}
                      dot={false}
                      name="API Calls"
                    />
                    <Line
                      type="monotone"
                      dataKey="activeUsers"
                      stroke="#23211d"
                      strokeWidth={2}
                      dot={false}
                      name="Active Users"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-gray-500">
                    <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No usage data available yet</p>
                    <p className="text-xs mt-1">Data will appear as users access the platform</p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Usage by Subscription Tier */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <PieChart className="w-5 h-5 text-purple-600" />
              Usage by Tier
            </h2>
            <div className="h-64">
              {platformUsage?.usageByTier && platformUsage.usageByTier.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={platformUsage.usageByTier}
                        dataKey="count"
                        nameKey="tier"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ tier, percentage }: any) => `${formatTierName(String(tier))}: ${percentage}%`}
                      >
                        {platformUsage.usageByTier.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value: number, name: string) => [formatNumber(value), name]}
                      />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 space-y-2">
                    {platformUsage.usageByTier.map((item, index) => (
                      <div key={item.tier} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: COLORS[index % COLORS.length] }}
                          />
                          <span className="text-sm font-medium text-gray-700">{formatTierName(item.tier)}</span>
                        </div>
                        <span className="text-sm text-gray-600">{formatNumber(item.count)}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-gray-500">
                    <PieChart className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No subscription data yet</p>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Usage by Type */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-green-600" />
              Usage by Feature
            </h2>
            <div className="h-64">
              {platformUsage?.usageByType && platformUsage.usageByType.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={platformUsage.usageByType.map(d => ({ ...d, label: formatFeatureName(d.type) }))} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2ddd1" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis dataKey="label" type="category" tick={{ fontSize: 12 }} width={120} />
                    <Tooltip
                      formatter={(value: number) => [formatNumber(value), 'API Calls']}
                      contentStyle={{
                        backgroundColor: '#fbf9f4',
                        border: '1px solid #e2ddd1',
                        borderRadius: '8px',
                      }}
                    />
                    <Bar dataKey="count" fill="#5d7148" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center text-gray-500">
                    <BarChart3 className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No feature usage data yet</p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Recent Activity */}
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-600" />
              Recent Activity
            </h2>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {recentActivity.length === 0 ? (
                <p className="text-gray-500 text-center py-4">No recent activity</p>
              ) : (
                recentActivity.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div
                      className={`p-2 rounded-lg ${
                        entry.action.includes('created')
                          ? 'bg-green-100'
                          : entry.action.includes('deleted')
                          ? 'bg-red-100'
                          : 'bg-blue-100'
                      }`}
                    >
                      {entry.action.includes('user') ? (
                        <Users
                          className={`w-4 h-4 ${
                            entry.action.includes('created')
                              ? 'text-green-600'
                              : entry.action.includes('deleted')
                              ? 'text-red-600'
                              : 'text-blue-600'
                          }`}
                        />
                      ) : (
                        <Building2
                          className={`w-4 h-4 ${
                            entry.action.includes('created')
                              ? 'text-green-600'
                              : entry.action.includes('deleted')
                              ? 'text-red-600'
                              : 'text-blue-600'
                          }`}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {getActionLabel(entry.action)}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {(entry.details as any).email || (entry.details as any).name || (entry.resourceId ? `ID: ${entry.resourceId}` : '')}
                      </p>
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {formatDate(entry.createdAt)}
                    </span>
                  </div>
                ))
              )}
            </div>
            <button
              onClick={() => navigate('/super-admin/audit-log')}
              className="mt-4 w-full py-2 text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center gap-1"
            >
              View All Activity
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </Card>
        </div>

        {/* New Users Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              New Users
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-xl">
                <p className="text-sm text-blue-600 font-medium">Last 7 Days</p>
                <p className="text-3xl font-bold text-blue-900 mt-1">
                  {dashboardStats?.newUsers7d || 0}
                </p>
              </div>
              <div className="p-4 bg-purple-50 rounded-xl">
                <p className="text-sm text-purple-600 font-medium">Last 30 Days</p>
                <p className="text-3xl font-bold text-purple-900 mt-1">
                  {dashboardStats?.newUsers30d || 0}
                </p>
              </div>
            </div>
          </Card>

          {/* Revenue Metrics - Hidden until payment system is implemented */}
          {dashboardStats && dashboardStats.revenueMonthly && dashboardStats.revenueMonthly > 0 && (
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Revenue Metrics
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-green-50 rounded-xl">
                  <p className="text-sm text-green-600 font-medium">Monthly Revenue</p>
                  <p className="text-3xl font-bold text-green-900 mt-1">
                    {formatCurrency(dashboardStats.revenueMonthly)}
                  </p>
                  {dashboardStats.revenueGrowth && dashboardStats.revenueGrowth !== 0 && (
                    <div className={`flex items-center gap-1 mt-2 ${dashboardStats.revenueGrowth > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {dashboardStats.revenueGrowth > 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                      <span className="text-sm font-medium">{dashboardStats.revenueGrowth > 0 ? '+' : ''}{dashboardStats.revenueGrowth.toFixed(1)}%</span>
                    </div>
                  )}
                </div>
                <div className="p-4 bg-yellow-50 rounded-xl">
                  <p className="text-sm text-yellow-700 font-medium">Avg. Revenue/User</p>
                  <p className="text-3xl font-bold text-yellow-900 mt-1">
                    {formatCurrency(Math.round(dashboardStats.revenueMonthly / dashboardStats.totalUsers))}
                  </p>
                  <div className="flex items-center gap-1 mt-2 text-gray-600">
                    <span className="text-sm">per month</span>
                  </div>
                </div>
              </div>
            </Card>
          )}
        </div>


        {/* IP Cross-Match Alerts */}
        {ipMatches.length > 0 && (
          <div className="mt-8 bg-white rounded-xl shadow-sm border border-red-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-red-600" />
              IP Cross-Match Alerts
              <span className="ml-2 px-2 py-0.5 text-xs font-bold bg-red-100 text-red-700 rounded-full">
                {ipMatches.length}
              </span>
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Users from different companies logging in from the same IP address or subnet
            </p>
            <div className="space-y-4">
              {ipMatches.map((match, idx) => (
                <div key={idx} className="p-4 bg-red-50 border border-red-100 rounded-xl">
                  <div className="flex items-center gap-2 mb-3">
                    <Globe className="w-4 h-4 text-red-500" />
                    <span className="font-mono text-sm font-medium text-red-800">{match.subnet || match.ip_address}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {match.users?.map((user: any, uidx: number) => (
                      <div key={uidx} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-red-100">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{user.email}</p>
                          <p className="text-xs text-gray-500">{user.company_name || 'No company'}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">{user.login_count} logins</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Last overlap: {match.last_overlap ? new Date(match.last_overlap).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Entity Views - Opened Carriers/Shippers/Brokers */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Link2 className="w-5 h-5 text-blue-600" />
            Opened Carriers / Shippers / Brokers
          </h2>
          {entityViews.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 font-medium text-gray-500">User</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Type</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-500">Entity ID</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">Views</th>
                    <th className="text-right py-3 px-4 font-medium text-gray-500">Last Viewed</th>
                  </tr>
                </thead>
                <tbody>
                  {entityViews.map((view, idx) => (
                    <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <p className="font-medium text-gray-900">{view.email}</p>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          view.entity_type === 'carrier' ? 'bg-blue-100 text-blue-700' :
                          view.entity_type === 'shipper' ? 'bg-green-100 text-green-700' :
                          'bg-purple-100 text-purple-700'
                        }`}>
                          {(view.entity_type || '').charAt(0).toUpperCase() + (view.entity_type || '').slice(1)}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-mono text-gray-700">{view.entity_id}</td>
                      <td className="py-3 px-4 text-right text-gray-700">{view.view_count}</td>
                      <td className="py-3 px-4 text-right text-gray-500 text-xs">
                        {view.last_viewed ? new Date(view.last_viewed).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <Link2 className="w-8 h-8 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500 text-sm">No entity views tracked yet</p>
              <p className="text-gray-400 text-xs mt-1">Data will appear as users browse carriers, shippers, and brokers</p>
            </div>
          )}
        </div>

        {/* Admin Settings */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Server className="w-5 h-5 text-blue-600" />
            Admin Settings
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/admin/smtp-settings')}
              className="flex items-center gap-4 w-full text-left p-4 cursor-pointer border border-gray-200 bg-white rounded-xl transition-all hover:bg-gray-50 hover:border-gray-300"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">SMTP Settings</h3>
                <p className="text-sm text-gray-500">Configure email server</p>
              </div>
            </button>
            <button
              onClick={() => navigate('/admin/registration-requests')}
              className="flex items-center gap-4 w-full text-left p-4 cursor-pointer border border-gray-200 bg-white rounded-xl transition-all hover:bg-gray-50 hover:border-gray-300"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-yellow-100">
                <UserPlus className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Registration Requests</h3>
                <p className="text-sm text-gray-500">Review and approve new users</p>
              </div>
            </button>
            <button
              onClick={() => navigate('/system-metrics')}
              className="flex items-center gap-4 w-full text-left p-4 cursor-pointer border border-gray-200 bg-white rounded-xl transition-all hover:bg-gray-50 hover:border-gray-300"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-green-100">
                <TrendingUp className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Model Metrics</h3>
                <p className="text-sm text-gray-500">Bayesian model performance</p>
              </div>
            </button>
            <button
              onClick={() => navigate('/system-metrics')}
              className="flex items-center gap-4 w-full text-left p-4 cursor-pointer border border-gray-200 bg-white rounded-xl transition-all hover:bg-gray-50 hover:border-gray-300"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-indigo-100">
                <Server className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">System Health</h3>
                <p className="text-sm text-gray-500">Database, API & monitoring</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
